// The debrief's scheduling card (lane B5 S12, screens-walks §2.7): the *Debrief readiness* gate — Ready or Not Ready with
// every missing control named and linked to where it clears ("As Coordinator, I see Ready or Not Ready with reasons."
// "missing controls are explicit.") — above the scheduling card, where the Coordinator schedules the debrief without a
// vote ("Coordinator schedules but has no vote."). The Coordinator's read is the schedule projection: roles and rounds,
// never a name, a score or a concern. Readiness re-reads with every scheduling write ("readiness updates
// automatically"). Early booking stays allowed by default (R13, `scheduling.debrief_requires_ready`); when the rule asks
// for Ready the booking is refused with his sentence and the dialog keeps it.

import type { ReadinessControlOut, RosterOut, SchedulingDebriefReadinessOutput } from "@/api/tools";
import { debriefRecordAttendance, schedulingDebriefReadiness, schedulingOpen } from "@/api/tools";
import { newIdempotencyKey } from "@/api/runtime";
import { isClientRefusal, useRead } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { DEBRIEF_READINESS_STATUS, Gate, LOOP_LABELS, METRIC_LABELS, ROOTS_LABELS, type GateRow } from "@/mc";
import { READINESS_LABELS } from "./labels";
import { ScheduleSection, type HeldOfflineControl } from "./ScheduleSection";
import { openInput, openKey } from "./schedule";
import { ROLE_WORD, acts } from "./workbench";

const ROOTS_WORD: Readonly<Record<string, string>> = {
  respect: ROOTS_LABELS.respect, ownership: ROOTS_LABELS.ownership, opportunity: ROOTS_LABELS.opportunity,
  together: ROOTS_LABELS.together, service: ROOTS_LABELS.service,
};

/** A missing control in his words, its round, value or seat as data. */
export function controlText(c: ReadinessControlOut): string {
  switch (c.code) {
    case "rounds_held":
      return [LOOP_LABELS.finalInterviewLoop, c.round_label].filter(Boolean).join(" · ");
    case "scorecards_submitted":
      return `${READINESS_LABELS.scorecard}: ${c.author_name ?? c.round_label ?? ROLE_WORD[c.role ?? ""] ?? ""}`.trim();
    case "roots_coverage":
      return `${READINESS_LABELS.coverage}: ${ROOTS_WORD[c.value ?? ""] ?? c.value ?? c.gap ?? ROOTS_LABELS.roots}`;
    case "summary_released":
    case "summary_stale":
      return READINESS_LABELS.candidateSummary;
    case "roster_complete":
      return `${READINESS_LABELS.requiredAttendees}: ${ROLE_WORD[c.role ?? ""] ?? c.role ?? ""}`;
    default:
      return READINESS_LABELS.missingControl;
  }
}

function rows(found: SchedulingDebriefReadinessOutput): GateRow[] {
  const open: GateRow[] = found.controls.map((c, index) => ({
    status: "bad",
    text: controlText(c),
    href: c.route ?? undefined,
    hs: `sched.readiness.${index}`,
  }));
  const roster: GateRow[] = found.roster.map((r: RosterOut, index) => ({
    status: "ok",
    text: `${READINESS_LABELS.requiredAttendees}: ${r.name ?? ROLE_WORD[r.role] ?? r.role}`,
    hs: `sched.roster.${index}`,
  }));
  return [...open, ...roster];
}

export function DebriefSchedule({ rid, cid }: { rid: string; cid: string }) {
  const read = useRead(["scheduling.debrief_readiness", rid, cid], () => schedulingDebriefReadiness({ requisition_id: rid, cycle_id: cid }));
  const found = read.data;
  const gate = read.error && isClientRefusal(read.error) ? null : (
    <Gate
      title={METRIC_LABELS.debriefReadiness}
      verdict={found ? (found.ready ? DEBRIEF_READINESS_STATUS.ready : DEBRIEF_READINESS_STATUS.notReady) : undefined}
      ready={found?.ready}
      rows={found ? rows(found) : []}
      loading={!found && !read.error}
      mode="view"
    />
  );
  const offline = useOffline(rid, cid, found);
  return <ScheduleSection subject={{ kind: "debrief", rid, cid }} above={gate} heldOffline={offline} />;
}

/** F-158 (DC-237): the debrief's *Held offline — continue* — for the seats that take the roll (debrief.record_attendance),
 *  while nothing is booked for it in the app; the required attendees are the roll, ticked present. */
function useOffline(rid: string, cid: string, found: SchedulingDebriefReadinessOutput | undefined): HeldOfflineControl {
  const { manifest, me } = useSession();
  const subject = { kind: "debrief", rid, cid } as const;
  const request = useRead(openKey(subject), () => schedulingOpen(openInput(subject)), { retry: false });
  const meetings = request.data?.meetings ?? [];
  const booked = meetings.some((m) => m.state === "booked" || m.state === "held_tentative");
  const ended = request.data ? request.data.status === "completed" || request.data.status === "cancelled" : false;
  const parties = (found?.roster ?? [])
    .filter((r): r is RosterOut & { person_id: string } => !!r.person_id)
    .map((r) => ({ person_id: r.person_id, name: r.name ?? ROLE_WORD[r.role] ?? r.role, seat: r.role, required: true }));
  return {
    can: acts(manifest, me, "debrief.record_attendance") && !booked && !ended,
    attendees: parties,
    tool: "debrief.record_attendance",
    record: (input) => debriefRecordAttendance({
      requisition_id: rid,
      cycle_id: cid,
      attendance: parties.map((p) => ({ person_id: p.person_id, status: input.present[p.person_id] === false ? "absent" : "present" })),
      held_offline: true,
      offline_reason: input.offline_reason,
      held_at: input.held_at,
    }, newIdempotencyKey()),
  };
}
